import { useQuery } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import api from '../lib/api';

// Role-based permissions (mirror of backend ROLE_PERMISSIONS)
const ROLE_PERMISSIONS = {
  ceo: {
    dashboard: { view: true, create: true, edit: true, delete: true, approve: true },
    hr: { view: true, create: true, edit: true, delete: true, approve: true },
    finance: { view: true, create: true, edit: true, delete: true, approve: true },
    dfs: { view: true, create: true, edit: true, delete: true, approve: true },
    vault: { view: true, create: true, edit: true, delete: true, approve: true },
    teller: { view: true, create: true, edit: true, delete: true, approve: true },
    reports: { view: true, create: true, edit: true, delete: true, approve: true },
    settings: { view: true, create: true, edit: true, delete: true, approve: true },
    users: { view: true, create: true, edit: true, delete: true, approve: true },
  },
  admin: {
    dashboard: { view: true, create: true, edit: true, delete: true, approve: true },
    hr: { view: true, create: true, edit: true, delete: true, approve: true },
    finance: { view: true, create: true, edit: true, delete: false, approve: true },
    dfs: { view: true, create: true, edit: true, delete: false, approve: true },
    vault: { view: true, create: true, edit: true, delete: false, approve: true },
    teller: { view: true, create: true, edit: true, delete: false, approve: true },
    reports: { view: true, create: true, edit: false, delete: false, approve: false },
    settings: { view: true, create: true, edit: true, delete: false, approve: false },
    users: { view: true, create: true, edit: true, delete: false, approve: false },
  },
  manager: {
    dashboard: { view: true, create: false, edit: false, delete: false, approve: true },
    hr: { view: true, create: true, edit: true, delete: false, approve: true },
    finance: { view: true, create: true, edit: true, delete: false, approve: true },
    dfs: { view: true, create: true, edit: true, delete: false, approve: true },
    vault: { view: true, create: true, edit: true, delete: false, approve: true },
    teller: { view: true, create: true, edit: true, delete: false, approve: true },
    reports: { view: true, create: true, edit: false, delete: false, approve: false },
    settings: { view: false, create: false, edit: false, delete: false, approve: false },
    users: { view: true, create: false, edit: false, delete: false, approve: false },
  },
  hr: {
    dashboard: { view: true, create: false, edit: false, delete: false, approve: false },
    hr: { view: true, create: true, edit: true, delete: false, approve: false },
    finance: { view: false, create: false, edit: false, delete: false, approve: false },
    dfs: { view: false, create: false, edit: false, delete: false, approve: false },
    vault: { view: false, create: false, edit: false, delete: false, approve: false },
    teller: { view: false, create: false, edit: false, delete: false, approve: false },
    reports: { view: true, create: false, edit: false, delete: false, approve: false },
    settings: { view: false, create: false, edit: false, delete: false, approve: false },
    users: { view: false, create: false, edit: false, delete: false, approve: false },
  },
  accountant: {
    dashboard: { view: true, create: false, edit: false, delete: false, approve: false },
    hr: { view: true, create: false, edit: false, delete: false, approve: false },
    finance: { view: true, create: true, edit: true, delete: false, approve: false },
    dfs: { view: true, create: false, edit: false, delete: false, approve: false },
    vault: { view: true, create: false, edit: false, delete: false, approve: false },
    teller: { view: true, create: false, edit: false, delete: false, approve: false },
    reports: { view: true, create: true, edit: false, delete: false, approve: false },
    settings: { view: false, create: false, edit: false, delete: false, approve: false },
    users: { view: false, create: false, edit: false, delete: false, approve: false },
  },
  teller: {
    dashboard: { view: false, create: false, edit: false, delete: false, approve: false },
    hr: { view: false, create: false, edit: false, delete: false, approve: false },
    finance: { view: false, create: false, edit: false, delete: false, approve: false },
    dfs: { view: true, create: true, edit: true, delete: false, approve: false },
    vault: { view: false, create: false, edit: false, delete: false, approve: false },
    teller: { view: true, create: true, edit: true, delete: false, approve: false },
    reports: { view: false, create: false, edit: false, delete: false, approve: false },
    settings: { view: false, create: false, edit: false, delete: false, approve: false },
    users: { view: false, create: false, edit: false, delete: false, approve: false },
  },
  viewer: {
    dashboard: { view: true, create: false, edit: false, delete: false, approve: false },
    hr: { view: true, create: false, edit: false, delete: false, approve: false },
    finance: { view: true, create: false, edit: false, delete: false, approve: false },
    dfs: { view: true, create: false, edit: false, delete: false, approve: false },
    vault: { view: true, create: false, edit: false, delete: false, approve: false },
    teller: { view: true, create: false, edit: false, delete: false, approve: false },
    reports: { view: true, create: false, edit: false, delete: false, approve: false },
    settings: { view: false, create: false, edit: false, delete: false, approve: false },
    users: { view: false, create: false, edit: false, delete: false, approve: false },
  },
};

export function usePermissions() {
  const { user } = useAuth();
  
  // Optionally fetch permissions from server (for custom roles)
  const { data: serverPermissions } = useQuery({
    queryKey: ['permissions', user?.id],
    queryFn: () => api.get('/auth/permissions').then(res => res.data),
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  const role = user?.role || 'viewer';
  const permissions = serverPermissions?.permissions || ROLE_PERMISSIONS[role] || ROLE_PERMISSIONS.viewer;

  /**
   * Check if user can perform action on module
   * @param {string} module - Module name (dashboard, hr, finance, dfs, vault, teller, reports, settings, users)
   * @param {string} action - Action type (view, create, edit, delete, approve)
   * @returns {boolean}
   */
  const can = (module, action) => {
    const modulePerms = permissions[module];
    if (!modulePerms) return false;
    return modulePerms[action] === true;
  };

  /**
   * Check if user can view a module
   */
  const canView = (module) => can(module, 'view');
  
  /**
   * Check if user can create in a module
   */
  const canCreate = (module) => can(module, 'create');
  
  /**
   * Check if user can edit in a module
   */
  const canEdit = (module) => can(module, 'edit');
  
  /**
   * Check if user can delete in a module
   */
  const canDelete = (module) => can(module, 'delete');
  
  /**
   * Check if user can approve in a module
   */
  const canApprove = (module) => can(module, 'approve');

  /**
   * Check if user has any permission in a module
   */
  const hasModuleAccess = (module) => {
    const modulePerms = permissions[module];
    if (!modulePerms) return false;
    return Object.values(modulePerms).some(v => v === true);
  };

  /**
   * Check if user is CEO or Admin
   */
  const isAdmin = ['ceo', 'admin'].includes(role);

  /**
   * Check if user is a manager or higher
   */
  const isManagerOrHigher = ['ceo', 'admin', 'manager'].includes(role);

  return {
    role,
    permissions,
    can,
    canView,
    canCreate,
    canEdit,
    canDelete,
    canApprove,
    hasModuleAccess,
    isAdmin,
    isManagerOrHigher,
  };
}

export default usePermissions;
